/** Rotating greeting library for the empty-chat hero.
 *
 *  A flat pool of full, time-neutral lines in Artie's voice. One line is chosen
 *  per login and kept stable for the session (see `useSessionGreeting`), then
 *  personalized with the user's name (see `personalize`).
 *
 *  NAME HANDLING: put a `{name}` placeholder wherever the name should read best
 *  — a leading "Hi {name}, …?" for questions, a trailing ", {name}." for
 *  statements. It's replaced with the user's preferred name; when the user has
 *  no name, a "Hi/Hey {name}, " lead-in is dropped (and the question stands
 *  alone) or a trailing ", {name}" vocative is removed. A line WITHOUT `{name}`
 *  never gets a name — that's a "no name needed" greeting. */
export const GREETINGS: readonly string[] = [
  // Questions — leading "Hi {name}, …?"
  "Hey {name}, what's the mission today?",
  "Hi {name}, where should we start?",
  "Hi {name}, what's on your plate?",
  "Hi {name}, what are we tackling today?",
  "Hi {name}, what's the priority right now?",
  "Hi {name}, what's first today?",
  "Hi {name}, what do you need?",
  "Hi {name}, what are we building?",
  "Hi {name}, where do you want to begin?",
  "Hi {name}, what's the goal today?",
  "Hi {name}, how can I help?",
  // No name needed
  "Got a task? I'm listening.",
  "Anything on fire?",
  "What should we look at?",
  "What's the ask?",
  "Point me at something.",
  // Openers / statements — trailing ", {name}."
  "Ready when you are, {name}.",
  "Let's make something happen, {name}.",
  "Let's get after it, {name}.",
  "Let's dig in, {name}.",
  "Let's get to work, {name}.",
  "Let's knock some things out, {name}.",
  "I'm all yours, {name}.",
  "Let's make it a good one, {name}.",
  // "Set me loose.",
  "Here to help - just say the word, {name}.",
  "Let's do this, {name}.",
  // Warmer
  "Good to see you, {name}.",
  "Welcome back, {name}.",
];

/** Pick a greeting from the pool. `rand` (defaults to `Math.random()`) is a
 *  value in [0, 1); injectable so tests are deterministic. */
export function pickGreeting(rand: number = Math.random()): string {
  const i = Math.floor(rand * GREETINGS.length) % GREETINGS.length;
  return GREETINGS[i];
}

/** Personalize a greeting for display.
 *
 *  Greetings may carry a `{name}` placeholder. When a name is available it's
 *  substituted in; when it isn't, a leading "Hi/Hey {name}, " lead-in is
 *  dropped (and the remaining question re-capitalized) or a trailing/inline
 *  ", {name}" vocative is removed, so the line still reads. Greetings WITHOUT a
 *  placeholder are returned unchanged — they never get a name.
 *
 *    personalize("Hi {name}, where should we start?", "Kirtana")
 *      -> "Hi Kirtana, where should we start?"
 *    personalize("Hi {name}, where should we start?", null)
 *      -> "Where should we start?"
 *    personalize("Ready when you are, {name}.", "Kirtana")
 *      -> "Ready when you are, Kirtana."
 *    personalize("Ready when you are, {name}.", null)
 *      -> "Ready when you are."
 *    personalize("Got a task? I'm listening.", "Kirtana")
 *      -> "Got a task? I'm listening." */
export function personalize(greeting: string, name?: string | null): string {
  if (!greeting.includes("{name}")) return greeting;
  const trimmed = name?.trim();
  if (trimmed) return greeting.replace(/\{name\}/g, trimmed);
  const lead = /^(Hi|Hey)\s+\{name\},\s*/i;
  if (lead.test(greeting)) {
    const s = greeting.replace(lead, "");
    return s.charAt(0).toUpperCase() + s.slice(1);
  }
  return greeting.replace(/,?\s*\{name\}/g, "");
}
